import { isCostCentersEnabled } from '@/utils/feature-flags';
import Vue from 'vue';
import Router from 'vue-router';
import { RouteConfig } from 'vue-router';
import { defaultRouteHandler } from './handler';

const DashboardPage = () => import(/* webpackChunkName: "dashboard" */ '@/pages/dashboard/DashboardPage.vue');
const AccountPage = () => import(/* webpackChunkName: "company" */ '@/pages/company/AccountPage.vue');
const DriverManagementPage = () => import(/* webpackChunkName: "driver-management" */ '@/pages/driver-management/DriverManagementPage.vue');
const NotFoundPage = () => import(/* webpackChunkName: "base" */ '@/pages/static/not-found/NotFoundPage.vue');
const NotAuthorizedPage = () => import(/* webpackChunkName: "not-authorized" */ '@/pages/static/not-authorized/NotAuthorizedPage.vue');
const RentalPage = () => import(/* webpackChunkName: "rentals" */ '@/pages/rentals/RentalsPage.vue');
const PaymentsPage = () => import(/* webpackChunkName: "payments" */ '@/pages/payments/PaymentsPage.vue');
const MigrationPage = () => import(/* webpackChunkName: "migration" */ '@/pages/migration/MigrationPage.vue');
const NotBusinessCustomerPage = () => import(/* webpackChunkName: "customer" */ '@/pages/not-business-customer/NotBusinessCustomerPage.vue');
const CoDriverPage = () => import(/* webpackChunkName: "co-driver" */ '@/pages/co-driver/CoDriverPage.vue');
const DriverRequestPage = () => import(/* webpackChunkName: "driver-request" */ '@/pages/driver-request/DriverRequestPage.vue');
const ContactUsPage = () => import(/* webpackChunkName: "contact-us" */ '@/pages/contact-us/ContactUsPage.vue');
const CreateAccountPage = () => import(/* webpackChunkName: "create-account" */ '@/pages/create-account/CreateAccountPage.vue');
// const JoinShareNowPage = () => import(/* webpackChunkName: "create-account" */ '@/pages/create-account-beta/JoinShareNowPage.vue');
// const CreateCompanyAccountPage = () => import(/* webpackChunkName: "company-registration" */ '@/pages/create-account-beta/CreateCompanyAccountPage.vue');
// const JoinCompanyPage = () => import(/* webpackChunkName: "company-registration" */ '@/pages/join-company/JoinCompanyPage.vue');
// const VerifyEmailPage = () => import(/* webpackChunkName: "company-registration" */ '@/pages/join-company/VerifyEmailPage.vue');
const EditProfilePage = () => import(/* webpackChunkName: "edit-profile" */ '@/pages/edit-profile/EditProfilePage.vue');
const CostCentersListPage = () => import(/* webpackChunkName: "group-list" */ '@/pages/cost-centers/list/CostCentersListPage.vue');
const CostCenterDetailsPage = () => import(/* webpackChunkName: "group-details" */ '@/pages/cost-centers/details/CostCenterDetailsPage.vue');
const CarOnDemandPage = () => import(/* webpackChunkName: "group-details" */ '@/pages/car-on-demand/CarOnDemandPage.vue');
// const EmailConfirmationPage = () => import('@/pages/create-account-beta/EmailConfirmationPage.vue');
// Disable B2B Keycloak infra https://urbandrive.atlassian.net/browse/MOPS-2434
// const RegistrationPage = () => import(/* webpackChunkName: "registration" */ '@/pages/registration/RegistrationPage.vue');
// const RegistrationSuccessPage = () => import(/* webpackChunkName: "company-registration" */ '@/pages/registration/registration-success/RegistrationSuccessPage.vue');

Vue.use(Router);
interface RouterConfig {
  routes: RouteConfig[];
  scrollBehavior: () => void;
}

const routerConfig: RouterConfig = {
  routes: [
    {
      path: '/',
      redirect: {
        name: 'Dashboard',
      },
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: DashboardPage,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/account',
      name: 'Account',
      component: AccountPage,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/drivers',
      name: 'Drivers',
      component: DriverManagementPage,
      children: [
        {
          path: ':tab',
          component: DriverManagementPage,
          name: 'driver-management-tab',
          meta: {
            requiresAuth: true,
          },
        },
      ],
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/rentals',
      name: 'Rentals',
      component: RentalPage,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/payments',
      name: 'Payments',
      component: PaymentsPage,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '/migration',
      name: 'Migration',
      component: MigrationPage,
      meta: {
        requiresMigration: true,
      },
    },
    {
      component: NotBusinessCustomerPage,
      name: 'not-a-business-customer-page',
      path: '/not-a-business-customer',
    },
    {
      component: CoDriverPage,
      name: 'co-driver',
      path: '/co-driver',
      meta: {
        requiresCustomerAuth: true,
      },
    },
    {
      component: DriverRequestPage,
      name: 'driver-request',
      path: '/driver-request',
      meta: {
        requiresCustomerAuth: true,
      },
    },
    // Disable B2B Keycloak infra https://urbandrive.atlassian.net/browse/MOPS-2434
    // {
    //   component: RegistrationPage,
    //   name: 'registration',
    //   path: '/registration',
    // },
    // {
    //   component: RegistrationSuccessPage,
    //   name: 'registration-success',
    //   path: '/registration/success',
    // },
    {
      component: ContactUsPage,
      name: 'contact-us',
      path: '/contact-us/:leadSource/:specificLeadSource?',
    },
    {
      component: CreateAccountPage,
      name: 'create-account',
      path: '/create-account',
    },
    {
      component: CreateAccountPage,
      name: 'create-account-campaign',
      path: '/create-account-campaign/:leadSourceSpecific/:referralId?',
    },
    {
      component: CarOnDemandPage,
      name: 'car-on-demand-form',
      path: '/car-on-demand-form',
    },
    {
      path: '/edit-profile',
      name: 'edit-profile',
      component: EditProfilePage,
      meta: {
        requiresB2BAuth: true,
      },
    },
    {
      path: '/beta',
      name: 'beta',
      component: AccountPage,
      meta: {
        requiresB2BAuth: true,
      },
    },
    {
      name: 'cost-centers',
      path: '/cost-centers',
      component: CostCentersListPage,
      meta: {
        requiresAuth: true,
        disabled: !isCostCentersEnabled(),
      },
    },
    {
      name: 'cost-center-details',
      path: '/cost-centers/:id',
      component: CostCenterDetailsPage,
      meta: {
        requiresAuth: true,
      },
    },
    {
      name: 'forbidden',
      path: '/forbidden',
      component: NotAuthorizedPage,
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: '*',
      name: 'notFound',
      component: NotFoundPage,
    },
  ],
  scrollBehavior() {
    window.scrollTo(0, 0);
  },
};

/*const companyReggieFeatureFlag = process.env.VUE_APP_COMPANY_REGGIE_FEATURE_FLAG === 'true';

if (companyReggieFeatureFlag) {
  routerConfig.routes.push(
    {
      component: JoinShareNowPage,
      name: 'join-share-now',
      path: '/join-free2move',
    },
    {
      component: CreateCompanyAccountPage,
      name: 'create-company-account',
      path: '/create-company-account',
    },
    {
      component: EmailConfirmationPage,
      name: 'join-share-now-email-confirmation',
      path: '/join-free2move/email',
    },
    {
      component: VerifyEmailPage,
      name: 'join-company',
      path: '/join-company/:country/:registrationToken',
    },
    {
      component: JoinCompanyPage,
      name: 'join-company-confirm',
      path: '/join-company/:country/:registrationToken/confirm',
      meta: {
        requiresCustomerAuth: true,
      },
    },
  );
}*/

function iterateNestedRoutes(routes: RouteConfig[], parentRoute?: RouteConfig) {
  routes.forEach((route: RouteConfig) => {
    if (route.meta && parentRoute && route.path !== '') {
      route.meta.parentName = parentRoute.name;
      route.meta.parentPath = parentRoute.path;
    }
    if (route.children) {
      iterateNestedRoutes(route.children, route);
    }
  });
}

iterateNestedRoutes(routerConfig.routes);
const router = new Router(routerConfig);
router.beforeEach(defaultRouteHandler);

export default router;
